/**
 * Configuration for custom tool display names in the chat UI.
 * 
 * This file defines user-friendly messages for tools when they are
 * in progress (pending) or completed (success).
 * 
 * Usage:
 * - Add new tools to the TOOL_DISPLAY_NAMES object
 * - Each tool should have 'pending' and 'completed' messages
 * - If a tool is not in this config, it will use default display names
 */

export interface ToolDisplayConfig {
  pending: string;
  completed: string;
}

/**
 * Mapping of tool names to their display configurations.
 * 
 * @example
 * 'read_skill_content': {
 *   pending: 'Activating skills',
 *   completed: 'Skill activated'
 * }
 */
export const TOOL_DISPLAY_NAMES: Record<string, ToolDisplayConfig> = {
  // Skills
  'read_skill_content': {
    pending: 'Activating skills',
    completed: 'Skill activated',
  },
  
  // Add more tool configurations here as needed
  // Example:
  // 'fetch_data': {
  //   pending: 'Fetching data',
  //   completed: 'Data fetched',
  // },
  // 'process_document': {
  //   pending: 'Processing document',
  //   completed: 'Document processed',
  // },
};

/**
 * Get user-friendly display name for a tool based on its status.
 * 
 * @param toolName - The internal tool name (e.g., 'read_skill_content')
 * @param status - The current status ('pending' or 'completed')
 * @returns User-friendly display string
 */
export const getToolDisplayName = (toolName: string, status: 'pending' | 'completed'): string => {
  const config = TOOL_DISPLAY_NAMES[toolName];
  
  if (config) {
    return config[status];
  }
  
  // Default display for tools not in the config
  return status === 'pending' 
    ? `Calling ${toolName}...` 
    : `${toolName} completed`;
};
